import { useState, useEffect } from 'react';

import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from 'next-themes';
import {
  LayoutDashboard, Activity, BrainCircuit, Send, Video, Bot,
  Radio, BarChart2, Settings, Bell, Search, TrendingUp, TrendingDown,
  MoreHorizontal, ChevronRight, ArrowUpRight, Sun, Moon, X
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend
} from 'recharts';
import CookieConsent from '../components/CookieConsent';
import LocaleLink from '../components/LocaleLink';

const LOGO_URL = 'https://quidory.com/media/quidory-logo.png';

const publishingData = [
  { day: 'Mon', posts: 14, videos: 3 },
  { day: 'Tue', posts: 22, videos: 5 },
  { day: 'Wed', posts: 18, videos: 4 },
  { day: 'Thu', posts: 28, videos: 7 },
  { day: 'Fri', posts: 24, videos: 6 },
  { day: 'Sat', posts: 19, videos: 4 },
  { day: 'Sun', posts: 31, videos: 9 },
];

const publishingDataAr = [
  { day: 'الإث', posts: 14, videos: 3 },
  { day: 'الثل', posts: 22, videos: 5 },
  { day: 'الأر', posts: 18, videos: 4 },
  { day: 'الخم', posts: 28, videos: 7 },
  { day: 'الجم', posts: 24, videos: 6 },
  { day: 'السب', posts: 19, videos: 4 },
  { day: 'الأح', posts: 31, videos: 9 },
];

const contentQueue = {
  en: [
    { title: 'Bitcoin Weekly Analysis', type: 'Post', channel: 'Telegram', time: 'Today 3:00 PM', status: 'pending' },
    { title: 'ETH Technical Breakdown', type: 'Post', channel: 'WhatsApp', time: 'Today 5:00 PM', status: 'pending' },
    { title: 'Market Weekly Report', type: 'Video', channel: 'YouTube', time: 'Tomorrow', status: 'draft' },
    { title: 'Portfolio Management Tips', type: 'Post', channel: 'Telegram', time: 'Tomorrow', status: 'ready' },
    { title: 'DeFi Trend Analysis', type: 'Post', channel: 'WhatsApp', time: 'Thu 10 AM', status: 'ready' },
  ],
  ar: [
    { title: 'تحليل البيتكوين الأسبوعي', type: 'منشور', channel: 'تيليغرام', time: 'اليوم ٣:٠٠ م', status: 'pending' },
    { title: 'تحليل إيثريوم التقني', type: 'منشور', channel: 'واتساب', time: 'اليوم ٥:٠٠ م', status: 'pending' },
    { title: 'تقرير السوق الأسبوعي', type: 'فيديو', channel: 'يوتيوب', time: 'غداً', status: 'draft' },
    { title: 'نصائح إدارة المحفظة', type: 'منشور', channel: 'تيليغرام', time: 'غداً', status: 'ready' },
    { title: 'تحليل اتجاهات ديفاي', type: 'منشور', channel: 'واتساب', time: 'الخميس ١٠ص', status: 'ready' },
  ],
};

const alerts = {
  en: [
    { msg: 'BTC crossed $95,000 resistance', time: '2m ago', type: 'up' },
    { msg: 'New intelligence signal: ETH accumulation pattern', time: '14m ago', type: 'neutral' },
    { msg: 'SOL dropped below key support at $180', time: '1h ago', type: 'down' },
    { msg: 'Post Publisher: 3 posts published successfully', time: '2h ago', type: 'neutral' },
  ],
  ar: [
    { msg: 'بيتكوين تجاوز مقاومة $٩٥,٠٠٠', time: 'منذ ٢د', type: 'up' },
    { msg: 'إشارة جديدة: نمط تجميع إيثريوم', time: 'منذ ١٤د', type: 'neutral' },
    { msg: 'سولانا انخفضت دون دعم $١٨٠', time: 'منذ ١س', type: 'down' },
    { msg: 'Post Publisher: تم نشر ٣ منشورات', time: 'منذ ٢س', type: 'neutral' },
  ],
};

const navItemsEn = [
  { icon: LayoutDashboard, label: 'Overview', active: true },
  { icon: Activity, label: 'Intelligence Feed' },
  { icon: BrainCircuit, label: 'AI Analyzer' },
  { icon: Send, label: 'Post Publisher' },
  { icon: Video, label: 'Video Publisher' },
  { icon: Bot, label: 'AI Agents' },
  { icon: Radio, label: 'Channels' },
  { icon: BarChart2, label: 'Analytics' },
  { icon: Settings, label: 'Settings' },
];

const navItemsAr = [
  { icon: LayoutDashboard, label: 'نظرة عامة', active: true },
  { icon: Activity, label: 'خلاصة الذكاء' },
  { icon: BrainCircuit, label: 'المحلل الذكي' },
  { icon: Send, label: 'Post Publisher' },
  { icon: Video, label: 'Video Publisher' },
  { icon: Bot, label: 'الوكلاء الذكيون' },
  { icon: Radio, label: 'القنوات' },
  { icon: BarChart2, label: 'التحليلات' },
  { icon: Settings, label: 'الإعدادات' },
];

const statusColors = {
  pending: 'bg-amber-500/10 text-amber-500',
  draft: 'bg-muted text-muted-foreground',
  ready: 'bg-emerald-500/10 text-emerald-500',
};

const DashboardPage = () => {
  const { language, toggleLanguage, isRTL } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => setMounted(true), []);

  const navItems = language === 'ar' ? navItemsAr : navItemsEn;
  const data = language === 'ar' ? publishingDataAr : publishingData;
  const queue = contentQueue[language];
  const alertList = alerts[language];
  const isAr = language === 'ar';

  const stats = isAr ? [
    { label: 'إجمالي المحتوى المنشور', value: '١,٢٤٧', change: '+١٢٪', up: true },
    { label: 'الوكلاء النشطون', value: '٣', change: '+١', up: true },
    { label: 'القنوات المتصلة', value: '٥', change: '', up: null },
    { label: 'تقارير اليوم', value: '١٨', change: '+٤', up: true },
  ] : [
    { label: 'Total Content Published', value: '1,247', change: '+12%', up: true },
    { label: 'Active AI Agents', value: '3', change: '+1', up: true },
    { label: 'Channels Connected', value: '5', change: '', up: null },
    { label: 'Intelligence Reports Today', value: '18', change: '+4', up: true },
  ];

  const Sidebar = ({ mobile = false }) => (
    <div className={`flex flex-col h-full bg-card border-e border-border ${mobile ? 'w-full' : 'w-60'}`}>
      {/* Logo */}
      <div className="p-5 border-b border-border">
        <LocaleLink to="/" className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="" className="h-7 w-7 object-contain" />
          <span className="font-semibold text-foreground text-sm">
            {isAr ? 'كويدوري' : 'Quidory'}
          </span>
        </LocaleLink>
      </div>

      {/* Demo badge */}
      <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-gold/10 border border-gold/20">
        <p className="text-xs text-gold font-medium">
          {isAr ? 'وضع التجريبي' : 'Demo Mode'}
        </p>
        <p className="text-[10px] text-gold/70 mt-0.5">
          {isAr ? 'بيانات تجريبية للعرض' : 'Sample data for preview'}
        </p>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 mt-2 space-y-0.5 overflow-y-auto no-scrollbar">
        {navItems.map((item, i) => {
          const Icon = item.icon;
          return (
            <button
              key={i}
              data-testid={`sidebar-nav-${i}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-start ${
                item.active
                  ? 'bg-foreground/5 text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* User */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
            <span className="text-gold text-xs font-bold">{isAr ? 'م' : 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-foreground truncate">
              {isAr ? 'المستخدم التجريبي' : 'Demo User'}
            </p>
            <p className="text-[10px] text-muted-foreground truncate">
              {isAr ? 'الخطة المحترفة' : 'Professional Plan'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex shrink-0">
        <Sidebar />
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-72 flex flex-col">
            <Sidebar mobile />
          </div>
          <div className="flex-1 bg-black/50" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-4 sm:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-1.5 text-muted-foreground hover:text-foreground"
              onClick={() => setSidebarOpen(true)}
            >
              <LayoutDashboard className="h-5 w-5" />
            </button>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted border border-border w-56">
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground">{isAr ? 'بحث...' : 'Search...'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="dash-lang-toggle"
              onClick={toggleLanguage}
              className="px-2.5 py-1 text-xs font-semibold rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              {language === 'ar' ? 'EN' : 'AR'}
            </button>
            {mounted && (
              <button
                data-testid="dash-theme-toggle"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-colors"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            )}
            <button className="relative p-1.5 text-muted-foreground hover:text-foreground">
              <Bell className="h-4 w-4" />
              <span className="absolute top-1 end-1 h-1.5 w-1.5 rounded-full bg-gold" />
            </button>
            <div className="h-7 w-7 rounded-full bg-gold/20 flex items-center justify-center">
              <span className="text-gold text-xs font-bold">{isAr ? 'م' : 'U'}</span>
            </div>
          </div>
        </header>

        {/* Scrollable Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-background">
          {/* Welcome */}
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-foreground">
              {isAr ? 'صباح الخير 👋' : 'Good morning 👋'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isAr ? 'هذا ملخص نشاطك اليوم' : "Here's a summary of your activity today"}
            </p>
          </div>

          {/* Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {stats.map((stat, i) => (
              <div
                key={i}
                data-testid={`stat-card-${i}`}
                className="bg-card border border-border rounded-xl p-4 hover:border-border/80 transition-colors"
              >
                <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-light text-foreground">{stat.value}</span>
                  {stat.change && (
                    <span className={`text-xs font-medium mb-0.5 flex items-center gap-0.5 ${stat.up ? 'text-emerald-500' : 'text-red-500'}`}>
                      {stat.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {stat.change}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Chart + Alerts Row */}
          <div className="grid lg:grid-cols-5 gap-4 mb-4">
            {/* Chart */}
            <div className="lg:col-span-3 bg-card border border-border rounded-xl p-5" data-testid="publishing-chart">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    {isAr ? 'نشاط النشر' : 'Publishing Activity'}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isAr ? 'آخر ٧ أيام' : 'Last 7 days'}
                  </p>
                </div>
                <button className="text-muted-foreground hover:text-foreground">
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorPosts" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorVideos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#d4af37" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#d4af37" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: 'hsl(221 55% 8%)', border: '1px solid hsl(217 33% 17%)', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#f8fafc' }}
                  />
                  <Area type="monotone" dataKey="posts" stroke="#6366f1" fill="url(#colorPosts)" strokeWidth={2} name={isAr ? 'منشورات' : 'Posts'} />
                  <Area type="monotone" dataKey="videos" stroke="#d4af37" fill="url(#colorVideos)" strokeWidth={2} name={isAr ? 'فيديوهات' : 'Videos'} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Alerts */}
            <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5" data-testid="alerts-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">
                  {isAr ? 'التنبيهات الأخيرة' : 'Recent Alerts'}
                </h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gold/10 text-gold">
                  {alertList.length}
                </span>
              </div>
              <div className="space-y-3">
                {alertList.map((alert, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${alert.type === 'up' ? 'bg-emerald-500' : alert.type === 'down' ? 'bg-red-500' : 'bg-gold'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground leading-snug">{alert.msg}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{alert.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Content Queue */}
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="content-queue">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {isAr ? 'قائمة انتظار المحتوى' : 'Content Queue'}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isAr ? 'مجدولة للنشر' : 'Scheduled for publishing'}
                </p>
              </div>
              <button className="text-xs text-gold hover:text-gold/80 flex items-center gap-1 font-medium">
                {isAr ? 'عرض الكل' : 'View all'}
                <ChevronRight className={`h-3.5 w-3.5 ${isRTL ? 'rtl-flip' : ''}`} />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-start px-5 py-3 text-muted-foreground font-medium">
                      {isAr ? 'المحتوى' : 'Content'}
                    </th>
                    <th className="text-start px-4 py-3 text-muted-foreground font-medium">
                      {isAr ? 'النوع' : 'Type'}
                    </th>
                    <th className="text-start px-4 py-3 text-muted-foreground font-medium hidden sm:table-cell">
                      {isAr ? 'القناة' : 'Channel'}
                    </th>
                    <th className="text-start px-4 py-3 text-muted-foreground font-medium hidden md:table-cell">
                      {isAr ? 'الوقت' : 'Scheduled'}
                    </th>
                    <th className="text-start px-4 py-3 text-muted-foreground font-medium">
                      {isAr ? 'الحالة' : 'Status'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map((item, i) => (
                    <tr key={i} className="border-t border-border hover:bg-muted/30 transition-colors">
                      <td className="px-5 py-3.5">
                        <span className="text-foreground font-medium text-xs">{item.title}</span>
                      </td>
                      <td className="px-4 py-3.5 text-muted-foreground">{item.type}</td>
                      <td className="px-4 py-3.5 text-muted-foreground hidden sm:table-cell">{item.channel}</td>
                      <td className="px-4 py-3.5 text-muted-foreground hidden md:table-cell">{item.time}</td>
                      <td className="px-4 py-3.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColors[item.status]}`}>
                          {item.status === 'pending' ? (isAr ? 'معلق' : 'Pending') :
                           item.status === 'draft' ? (isAr ? 'مسودة' : 'Draft') :
                           (isAr ? 'جاهز' : 'Ready')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legal footer */}
          <div className="mt-10 pt-6 border-t border-border text-center" data-testid="dashboard-legal-footer">
            <p className="text-xs text-muted-foreground/60">
              {isAr ? '© ٢٠٢٥ كويدوري. جميع الحقوق محفوظة.' : '© 2025 Quidory. All rights reserved.'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground/50">
              {isAr ? 'تُدار بواسطة Al Yakhour LLC · وايومنغ، الولايات المتحدة' : 'Operated by Al Yakhour LLC · Wyoming, USA'}
            </p>
          </div>
        </main>
      </div>
      <CookieConsent />
    </div>
  );
};

export default DashboardPage;
